/**
 * Logger central com níveis (debug / info / warn / error) gateados por
 * {@code VITE_LOG_LEVEL}. Sprint 2 Fix#8.
 *
 * <p><b>Trade-off:</b> "console limpo em produção + audit trail completo
 * pro {@code logs:tail} remoto". Mensagens cujo nível fica abaixo do gate
 * são silenciadas no DevTools (não chamam {@code console.*}), mas
 * continuam sendo capturadas pelo {@link LogRingBuffer} — o operador pode
 * pegar tudo pelo comando remoto sem ter que ativar feature flag de log
 * verboso.</p>
 *
 * <p><b>Defaults:</b></p>
 * <ul>
 *   <li>Sem {@code VITE_LOG_LEVEL} setada → {@code debug} em dev / {@code info} em prod</li>
 *   <li>Valores aceitos: {@code debug}, {@code info}, {@code warn}, {@code error}</li>
 *   <li>Qualquer outro valor → fallback pro default acima</li>
 * </ul>
 *
 * <p><b>Uso:</b></p>
 * <pre>{@code
 *   import { logger } from '~/services/logging/logger';
 *
 *   logger.debug('detalhe interno raramente útil');     // só aparece se VITE_LOG_LEVEL=debug
 *   logger.info('cache montado: 8 items');              // dev + prod
 *   logger.warn('IndexedDB timed out, fallback remote'); // sempre aparece
 *   logger.error('falhou pra mapear conteúdo', err);    // sempre aparece
 * }</pre>
 *
 * <p>NB: o {@link LogRingBuffer} já intercepta {@code console.*} no boot do
 * app — então logs chamados via {@code console.log} diretamente também são
 * capturados, só não passam pelo gate. Migração de chamadas verbose pra este
 * logger é incremental.</p>
 */

import { logRingBuffer, type LogLevel } from './LogRingBuffer';

type GatingLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_ORDER: Record<GatingLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

function resolveDefaultLevel(): GatingLevel {
  // Vite expõe MODE via import.meta.env. Em build de produção (Tizen/webOS/
  // Android/web release) MODE === 'production'; em `npm run dev` é
  // 'development'. Fallback defensivo pra info se algo der errado.
  try {
    const mode = (import.meta as ImportMeta & { env?: { MODE?: string } }).env
      ?.MODE;
    return mode === 'production' ? 'info' : 'debug';
  } catch {
    return 'info';
  }
}

function parseLevel(raw: string | undefined): GatingLevel {
  const normalized = (raw ?? '').trim().toLowerCase();
  if (
    normalized === 'debug' ||
    normalized === 'info' ||
    normalized === 'warn' ||
    normalized === 'error'
  ) {
    return normalized;
  }
  return resolveDefaultLevel();
}

let currentLevel: GatingLevel = parseLevel(
  (import.meta as ImportMeta & { env?: { VITE_LOG_LEVEL?: string } }).env
    ?.VITE_LOG_LEVEL,
);

function shouldEmit(level: GatingLevel): boolean {
  return LEVEL_ORDER[level] >= LEVEL_ORDER[currentLevel];
}

function emit(level: GatingLevel, args: unknown[]): void {
  if (shouldEmit(level)) {
    // O LogRingBuffer já intercepta console.* — então chamar console
    // automaticamente grava no buffer + mostra no DevTools.
    (console[level] as (...a: unknown[]) => void)(...args);
  } else {
    // Abaixo do gate: grava direto no buffer, sem poluir DevTools.
    logRingBuffer.record(level as LogLevel, args);
  }
}

export const logger = {
  debug: (...args: unknown[]): void => emit('debug', args),
  info: (...args: unknown[]): void => emit('info', args),
  warn: (...args: unknown[]): void => emit('warn', args),
  error: (...args: unknown[]): void => emit('error', args),

  /** Retorna o nível ativo (útil pra debug / tests). */
  getLevel(): GatingLevel {
    return currentLevel;
  },

  /**
   * Override de nível em runtime — usar com cuidado, pensado pra testes ou
   * pra comando remoto futuro de troubleshooting ("aumenta verbosidade desse
   * tablet por 1h"). Não persiste — reset no próximo boot.
   */
  setLevel(level: GatingLevel): void {
    currentLevel = level;
  },
};

export type { GatingLevel as LoggerLevel };
